import { ComponentType, useMemo } from 'react';
import { createSelector } from 'reselect';

import ListItemAICoworker from 'flow/components/ui-components/ListItemAICoworker';
import ListItemPbxConnection from 'flow/components/ui-components/ListItemPbxConnection';
import ListItemPbxIvr from 'flow/components/ui-components/ListItemPbxIvr';
import ListItemPbxLiveChat from 'flow/components/ui-components/ListItemPbxLiveChat';
import ListItemPbxQueue from 'flow/components/ui-components/ListItemPbxQueue';
import ListItemPbxVoicemail from 'flow/components/ui-components/ListItemPbxVoicemail';
import ListItemSupervisor from 'flow/components/ui-components/ListItemSupervisor';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectCanAccessSupervisorView,
    selectOwnChatUserKey,
} from 'flow/selectors/own-extension-selectors';
import {
    selectFavoriteQueuesSortingData,
    selectQueuesSortingData,
    SUPERVISOR_VIEW_KEY,
} from 'flow/selectors/queue-selectors';
import { keepWhileEqual } from 'flow/selectors/selector-utils';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { sortChannels, sortPbxServices } from 'flow/utils/pbx-sorting-utils';
import {
    filterChannelsBySearchableInfo,
    filterPbxServiceBySearchableInfo,
} from 'flow/utils/pbx-utils';
import { sortQueuesSortingData } from 'flow/utils/pbx-utils-TW';
import {
    DepartmentState,
    PbxListDepartment,
    PbxListItem,
    PbxListRow,
} from './pbx-list-rows';

interface Options {
    filterText: string;
    departmentExpanded: DepartmentState;
    enabledDepartments: PbxListDepartment[];
}

const usePbxListRows = ({
    filterText,
    departmentExpanded,
    enabledDepartments,
}: Options) => {
    const { i18next } = useAppContext();
    const pbxListSelectors = useMemo(makePbxListSelectors, []);

    const favoriteQueues = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedFavoriteQueues(state, filterText),
    );

    const refers = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedRefers(state, filterText),
    );

    const queues = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedQueues(state, filterText),
    );

    const channels = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedChannels(state, filterText),
    );

    const voicemails = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedVoicemails(state, filterText),
    );

    const connections = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedConnections(state, filterText),
    );

    const aiCoworkers = useAppSelector((state) =>
        pbxListSelectors.selectFilteredSortedAICoworkers(state, filterText),
    );

    const hasSuperVisorView = useAppSelector(selectCanAccessSupervisorView);

    return useMemo(() => {
        const rows: PbxListRow[] = [];

        // A search shows every section open, so a title has nothing to fold.
        const canToggle = isEmptyNullOrUndefined(filterText);

        const addSection = (
            title: string,
            Component: ComponentType<any>,
            items: TypedMap<PbxListItem[]>,
            department: keyof DepartmentState,
        ) => {
            if (!items?.size) {
                return;
            }

            const isOpen = departmentExpanded[department] || !canToggle;
            rows.push({
                kind: 'sectionTitle',
                department,
                title,
                isOpen,
                canToggle,
            });

            if (isOpen) {
                items.forEach((item: PbxListItem) => {
                    rows.push(
                        item.get('key')
                            ? {
                                  kind: 'item',
                                  key: item.get('key'),
                                  department,
                                  item,
                                  Component,
                              }
                            : { kind: 'unfocusable' },
                    );
                });
            }
        };

        if (
            hasSuperVisorView &&
            enabledDepartments.includes('supervisorView')
        ) {
            rows.push({
                kind: 'item',
                key: SUPERVISOR_VIEW_KEY,
                department: 'supervisorView',
                Component: ListItemSupervisor,
            });
        }

        if (enabledDepartments.includes('favorites')) {
            addSection(
                i18next.t('General.Favorites'),
                ListItemPbxQueue,
                favoriteQueues,
                'favorites',
            );
        }

        if (enabledDepartments.includes('aiCoworkers')) {
            addSection(
                i18next.t('pbx.aicoworkers'),
                ListItemAICoworker,
                aiCoworkers,
                'aiCoworkers',
            );
        }

        if (enabledDepartments.includes('ivr')) {
            addSection(
                i18next.t('pbx.refers'),
                ListItemPbxIvr,
                refers,
                'refers',
            );
        }

        if (enabledDepartments.includes('queues')) {
            addSection(
                i18next.t('pbx.queues'),
                ListItemPbxQueue,
                queues,
                'queues',
            );
        }

        if (enabledDepartments.includes('liveChats')) {
            addSection(
                i18next.t('pbx.channels'),
                ListItemPbxLiveChat,
                channels,
                'channels',
            );
        }

        if (enabledDepartments.includes('voicemails')) {
            addSection(
                i18next.t('pbx.voicemails'),
                ListItemPbxVoicemail,
                voicemails,
                'voicemails',
            );
        }

        if (enabledDepartments.includes('connections')) {
            addSection(
                i18next.t('pbx.connections'),
                ListItemPbxConnection,
                connections,
                'connections',
            );
        }

        return rows;
    }, [
        filterText,
        departmentExpanded,
        enabledDepartments,
        hasSuperVisorView,
        favoriteQueues,
        aiCoworkers,
        refers,
        queues,
        channels,
        voicemails,
        connections,
        i18next,
    ]);
};

export default usePbxListRows;

const selectPbxFilter = (state: State, pbxFilter: string) => pbxFilter;
const selectRefers = (state: State) => state.pbx.get('refers');
const selectVoicemails = (state: State) => state.pbx.get('voicemails'); //TODO copied from old component: TODO: Quickfix, fix me in the API response instead!
const selectConnections = (state: State) => state.pbx.get('connections');
const selectChannels = (state: State) => state.channels.get('channels');
const selectAICoworkers = (state: State) =>
    state.extensions.get('aiCoworkersSortingData');

// The PBX tab and the number picker can show this list at the same time, with
// different search text. With one shared cache entry, each list would replace
// the other's, so every store update would filter and sort again.
const makePbxListSelectors = () => ({
    selectFilteredSortedFavoriteQueues: keepWhileEqual(
        createSelector(
            [selectFavoriteQueuesSortingData, selectPbxFilter],
            (favoriteQueuesSortingData, pbxFilter) =>
                sortQueuesSortingData(
                    filterPbxServiceBySearchableInfo(
                        favoriteQueuesSortingData,
                        pbxFilter,
                    ),
                ),
        ),
    ),
    selectFilteredSortedRefers: keepWhileEqual(
        createSelector([selectRefers, selectPbxFilter], (refers, pbxFilter) =>
            sortPbxServices(
                filterPbxServiceBySearchableInfo(refers, pbxFilter),
            ),
        ),
    ),
    selectFilteredSortedQueues: keepWhileEqual(
        createSelector(
            [selectQueuesSortingData, selectPbxFilter],
            (queuesSortingData, pbxFilter) =>
                sortQueuesSortingData(
                    filterPbxServiceBySearchableInfo(
                        queuesSortingData,
                        pbxFilter,
                    ),
                ),
        ),
    ),
    selectFilteredSortedChannels: keepWhileEqual(
        createSelector(
            [selectChannels, selectPbxFilter, selectOwnChatUserKey],
            (channels, pbxFilter, ownChatUserKey) =>
                sortChannels(
                    filterChannelsBySearchableInfo(channels, pbxFilter),
                    ownChatUserKey,
                ),
        ),
    ),
    selectFilteredSortedVoicemails: keepWhileEqual(
        createSelector(
            [selectVoicemails, selectPbxFilter],
            (voicemails, pbxFilter) =>
                sortPbxServices(
                    filterPbxServiceBySearchableInfo(voicemails, pbxFilter),
                ),
        ),
    ),
    selectFilteredSortedConnections: keepWhileEqual(
        createSelector(
            [selectConnections, selectPbxFilter],
            (connections, pbxFilter) =>
                sortPbxServices(
                    filterPbxServiceBySearchableInfo(connections, pbxFilter),
                ),
        ),
    ),
    selectFilteredSortedAICoworkers: keepWhileEqual(
        createSelector(
            [selectAICoworkers, selectPbxFilter],
            (aiCoworkers, pbxFilter) =>
                sortPbxServices(
                    filterPbxServiceBySearchableInfo(aiCoworkers, pbxFilter),
                ),
        ),
    ),
});
